/**
 * @file     documentRecommendations.ts
 * @readme   ../../CLAUDE.md
 * @purpose  Pure rules for property-specific due-diligence document requests.
 */

import type { ListingDetail } from '@/services/mls/mlsPropertyService';

export type DocumentCategory = 'ownership' | 'strata' | 'building' | 'leasehold' | 'rural' | 'new-home';

export interface DocumentRecommendation {
  id: string;
  label: string;
  category: DocumentCategory;
  reason: string;
}

type DocumentSignals = Pick<
  ListingDetail,
  | 'propertyType'
  | 'propertySubType'
  | 'commonInterest'
  | 'ownership'
  | 'associationFee'
  | 'landLeaseYn'
  | 'lotSizeSquareFeet'
  | 'yearBuilt'
>;

const documents = {
  disclosure: { id: 'property-disclosure', label: 'Property disclosure statement', category: 'ownership', reason: 'Review seller-disclosed conditions and known defects.' },
  title: { id: 'title-search', label: 'Title search', category: 'ownership', reason: 'Review registered ownership, charges, easements, and covenants.' },
  inspection: { id: 'inspection-reports', label: 'Inspection reports', category: 'building', reason: 'Review available property-condition findings.' },
  formB: { id: 'form-b', label: 'Form B', category: 'strata', reason: 'Review strata fees, balances, parking, storage, and notices.' },
  bylaws: { id: 'strata-bylaws', label: 'Bylaws and rules', category: 'strata', reason: 'Review rental, pet, renovation, and use restrictions.' },
  minutes: { id: 'strata-minutes', label: 'Council and AGM minutes', category: 'strata', reason: 'Review recent decisions, disputes, repairs, and planned work.' },
  depreciation: { id: 'depreciation-report', label: 'Depreciation report', category: 'strata', reason: 'Review the long-term repair plan and funding outlook.' },
  strataFinancials: { id: 'strata-financials', label: 'Budget and financial statements', category: 'strata', reason: 'Review operating costs, contingency funds, and special levies.' },
  strataInsurance: { id: 'strata-insurance', label: 'Strata insurance summary', category: 'strata', reason: 'Review building coverage, deductibles, and policy limits.' },
  permits: { id: 'permits', label: 'Building and renovation permits', category: 'building', reason: 'Confirm that significant work was properly permitted.' },
  survey: { id: 'survey-site-plan', label: 'Survey or site plan', category: 'building', reason: 'Review boundaries, structures, and encroachments.' },
  lease: { id: 'lease-agreement', label: 'Leasehold agreement', category: 'leasehold', reason: 'Review the remaining term, ground rent, renewal, and assignment rules.' },
  leaseCosts: { id: 'leasehold-costs', label: 'Ground rent and fee schedule', category: 'leasehold', reason: 'Review current and future leasehold carrying costs.' },
  zoning: { id: 'zoning-records', label: 'Zoning and land-use records', category: 'rural', reason: 'Review permitted uses and development constraints.' },
  well: { id: 'well-water-records', label: 'Well and water-quality records', category: 'rural', reason: 'Review water source, testing, and maintenance information.' },
  septic: { id: 'septic-records', label: 'Septic records', category: 'rural', reason: 'Review system permits, servicing, and condition information.' },
  warranty: { id: 'new-home-warranty', label: 'New-home warranty', category: 'new-home', reason: 'Review warranty coverage, dates, and provider information.' },
  newDisclosure: { id: 'developer-disclosure', label: 'Developer disclosure statement', category: 'new-home', reason: 'Review the project disclosure and material amendments.' },
} as const satisfies Record<string, DocumentRecommendation>;

export function buildDocumentRecommendations(listing: DocumentSignals, currentYear = new Date().getFullYear()): DocumentRecommendation[] {
  const subtype = `${listing.propertyType ?? ''} ${listing.propertySubType ?? ''}`.toLowerCase();
  const ownership = `${listing.commonInterest ?? ''} ${listing.ownership ?? ''}`.toLowerCase();
  const isStrata = (listing.associationFee ?? 0) > 0 || /strata/.test(ownership) || /apartment|condo|townhouse/.test(subtype);
  const isLeasehold = listing.landLeaseYn === true || /leasehold|land lease/.test(ownership);
  const isDetached = /single family|detached|house|duplex/.test(subtype);
  const isLandOrRural = /land|acreage|farm|rural/.test(subtype) || (listing.lotSizeSquareFeet ?? 0) >= 20_000;
  const isNewHome = listing.yearBuilt != null && listing.yearBuilt >= currentYear - 2;

  const out: DocumentRecommendation[] = [documents.disclosure, documents.title, documents.inspection];
  if (isStrata) out.push(documents.formB, documents.bylaws, documents.minutes, documents.depreciation, documents.strataFinancials, documents.strataInsurance);
  if (isDetached) out.push(documents.permits, documents.survey);
  if (isLeasehold) out.push(documents.lease, documents.leaseCosts);
  if (isLandOrRural) out.push(documents.zoning, documents.survey, documents.well, documents.septic);
  if (isNewHome) out.push(documents.warranty, documents.newDisclosure, documents.permits);

  return [...new Map(out.map((document) => [document.id, document])).values()];
}

export function supportsDocumentPackage(recommendations: DocumentRecommendation[]): boolean {
  const generic = new Set(['property-disclosure', 'title-search', 'inspection-reports']);
  return recommendations.some((document) => !generic.has(document.id));
}
